import type {
	SRT_Column,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'
import type { Grid, GridCellStub, GridRowStub } from '../../virt/types'

import React, { Fragment } from 'react'

import { TableBody, TableCell, TableRow } from '@/shared/components/ui/table'

import { useSRT_Rows } from '../../hooks/useSRT_Rows'
import { cn } from '../../lib/utils'
import {
	getCommonPinningStyles,
	parseFromValuesOrFunc,
} from '../../utils/utils'
import { TABLE_STUB_TYPE } from '../../virt/utils'
import { SRT_TableBodyCell } from './SRT_TableBodyCell'

export function SRT_TableBody<TData extends SRT_RowData>({
	table,
	grid,
	renderGroup,
	layoutMode,
	renderDetailPanel,
	renderNoData,
}: {
	table: SRT_TableInstance<TData>
	grid?: Grid<TData>
	layoutMode?: string
	renderGroup?: (props: {
		row: SRT_Row<TData>
		table: SRT_TableInstance<TData>
	}) => React.ReactNode
	renderNoData?: () => React.ReactNode
	renderDetailPanel?: (props: {
		row: SRT_Row<TData>
		table: SRT_TableInstance<TData>
	}) => React.ReactNode
}) {
	const {
		options: {
			localization,
			getRowClassName,
			divideX,
			divideY,
			shadcnTableBodyRowProps,
		},
	} = table
	const rows = useSRT_Rows(table)

	const isVirtulaized = !!grid?.rows

	const commonCellClassNames = cn({
		'border-r': divideX,
		'border-b': divideY,
	})
	if (!rows?.length) {
		return (
			<TableBody>
				<TableRow className='bg-transparent hover:bg-transparent'>
					<TableCell
						colSpan={table.getVisibleLeafColumns().length}
						className='sticky left-0 grid min-w-82'
					>
						{renderNoData?.() || (
							<span className='text-muted-foreground'>
								{localization.noResultsFound}
							</span>
						)}
					</TableCell>
				</TableRow>
			</TableBody>
		)
	}

	return (
		<TableBody>
			{isVirtulaized
				? grid?.rows.map((virtualRow) => {
						if (isVirtulaized && isGridRowStub(virtualRow.row)) {
							return (
								<tr
									key={virtualRow.row.key}
									className='h-[var(--data-row-height)]'
								>
									<td style={{ height: `${virtualRow.row.height}px` }} />
								</tr>
							)
						}
						const row = virtualRow.row as SRT_Row<TData>

						const visibleCells = row.getVisibleCells()
						const cells = virtualRow.cells
						if (row.groupingColumnId) {
							return (
								<TableRow
									key={row.id}
									data-testid='mt-group-row'
									data-rowid={row.id}
									className='h-[var(--data-row-height)]'
								>
									<TableCell
										colSpan={cells.length}
										className={cn('min-h-8 truncate border-b py-0', {
											'bg-table': row.getIsExpanded(),
											'bg-secondary': !row.getIsExpanded(),
										})}
									>
										{renderGroup?.({ row, table }) || 'NONE'}
									</TableCell>
								</TableRow>
							)
						}
						const tableRowProps = parseFromValuesOrFunc(
							shadcnTableBodyRowProps,
							{
								row,
								staticRowIndex: row.index,
								table,
							}
						)

						return (
							<TableRow
								{...tableRowProps}
								key={row.id}
								data-testid='mt-row'
								data-rowid={row.id}
								data-state={row.getIsSelected() ? 'selected' : undefined}
								className={cn(
									getRowClassName?.({ row, table }),
									tableRowProps?.className
								)}
							>
								{cells.map((virtualCell, cellIndex) => {
									if (isGridCellStub(virtualCell.column)) {
										return (
											<TableCell
												data-testid={`mt-cell-${cellIndex}:${row.index}`}
												colSpan={virtualCell.colSpan}
												key={virtualCell.column.key}
												className='h-[var(--data-row-height)] py-0'
												style={{
													width: virtualCell.column.width!,
													minWidth: virtualCell.column.width!,
													maxWidth: virtualCell.column.width!,
												}}
											/>
										)
									}
									const cell = visibleCells[virtualCell.index]

									return (
										<TableCell
											data-testid={`mt-cell-${cellIndex}:${row.index}`}
											data-columnid={cell.id}
											className={cn(
												'h-[var(--data-row-height)] truncate bg-inherit py-0',
												commonCellClassNames,
												{
													'border-l': cell.column.getIsPinned() === 'right',
												}
											)}
											key={`${row.id}_${cell.id}`}
											style={getCommonPinningStyles<TData>(
												cell.column,
												layoutMode
											)}
										>
											<SRT_TableBodyCell
												staticColumnIndex={virtualCell.index}
												staticRowIndex={row.index}
												cell={cell}
												table={table}
											/>
										</TableCell>
									)
								})}
							</TableRow>
						)
					})
				: rows.map((row) => {
						const visibleCells = row.getVisibleCells()
						if (row.groupingColumnId) {
							return (
								<TableRow
									data-testid='mt-group-row'
									data-rowid={row.id}
									key={row.id}
								>
									<TableCell
										colSpan={visibleCells.length}
										className={cn('min-h-8 truncate border-b py-0', {
											'bg-table': row.getIsExpanded(),
											'bg-secondary': !row.getIsExpanded(),
										})}
									>
										{renderGroup?.({ row, table }) || 'NONE'}
									</TableCell>
								</TableRow>
							)
						}
						const tableRowProps = parseFromValuesOrFunc(
							shadcnTableBodyRowProps,
							{
								row,
								staticRowIndex: row.index,
								table,
							}
						)

						return (
							<Fragment key={row.id}>
								<TableRow
									{...tableRowProps}
									key={row.id}
									data-testid='mt-row'
									data-rowid={row.id}
									data-state={row.getIsSelected() ? 'selected' : undefined}
									className={cn(
										getRowClassName?.({ row, table }),
										tableRowProps?.className
									)}
								>
									{visibleCells.map((cell, cellIndex) => (
										<TableCell
											data-testid={`mt-cell-${cellIndex}:${row.index}`}
											data-columnid={cell.id}
											className={cn(
												'h-[var(--data-row-height)] truncate bg-inherit py-0',
												commonCellClassNames,
												{
													'border-l': cell.column.getIsPinned() === 'right',
												}
											)}
											key={`${row.id}_${cell.id}`}
											style={getCommonPinningStyles<TData>(
												cell.column,
												layoutMode
											)}
										>
											<SRT_TableBodyCell
												staticColumnIndex={cellIndex}
												staticRowIndex={row.index}
												cell={cell}
												table={table}
											/>
										</TableCell>
									))}
								</TableRow>
								{row.getIsExpanded() &&
									row.getCanExpand() &&
									renderDetailPanel && (
										<TableRow
											data-testid='mt-detail-row'
											data-refrowid={row.id}
										>
											<TableCell
												colSpan={visibleCells.length}
												className='border-b'
											>
												{renderDetailPanel?.({ row, table }) || 'None'}
											</TableCell>
										</TableRow>
									)}
							</Fragment>
						)
					})}
		</TableBody>
	)
}
const isGridRowStub = <T extends SRT_RowData>(
	row: SRT_Row<T> | GridRowStub
): row is GridRowStub => (row as GridRowStub).type === TABLE_STUB_TYPE
const isGridCellStub = <T extends SRT_RowData>(
	column: SRT_Column<T> | GridCellStub
): column is GridCellStub => (column as GridCellStub).type === TABLE_STUB_TYPE
